import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, UserRound, Users } from 'lucide-react';
import { nationalIdText } from '@/lib/national-id';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { Alert, Checkbox, Field, Input, Select } from '@/components/ui/primitives';
import { PhoneText } from '@/components/ui/phone-text';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { RELATIONS } from './PatientContacts';

const EMPTY = { name: '', relation: 'guardian', phone: '', cnic: '', canConsent: true, isPrimary: false };

export function PatientContactPage() {
  const { patientId = '' } = useParams<{ patientId: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const returnTo = safeReturnTo(params.get('returnTo'), `/patients/${patientId}`);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      post(`/patients/${patientId}/contacts`, {
        name: form.name.trim(),
        relation: form.relation,
        phone: form.phone.trim() || undefined,
        cnic: form.cnic.trim() || undefined,
        canConsent: form.canConsent,
        isPrimary: form.isPrimary,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['patient', patientId] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const ready = form.name.trim().length >= 2;
  const dirty = !save.isSuccess && JSON.stringify(form) !== JSON.stringify(EMPTY);

  return (
    <FormScreen
      title={tr('opd', 'add_a_guardian_or_contact')}
      description={tr('opd', 'a_second_parent_an_attendant_or_whoever_may')}
      icon={Users}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('opd', 'details')} icon={UserRound}>
        <Field label={tr('opd', 'name')} required className="sm:col-span-2">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label={tr('opd', 'relation')} required>
          <Select value={form.relation} onChange={(e) => setForm({ ...form, relation: e.target.value })}>
            {RELATIONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={tr('opd', 'phone')}>
          <PhoneText value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={tr('opd', '03xxxxxxxxx')} />
        </Field>
        <Field label={nationalIdText().label}>
          <Input value={form.cnic} onChange={(e) => setForm({ ...form, cnic: e.target.value })} placeholder={nationalIdText().hint} />
        </Field>
      </FormSection>

      <FormSection title={tr('opd', 'consent')} icon={ShieldCheck} columns={1}>
        <Checkbox
          checked={form.canConsent}
          onCheckedChange={(checked) => setForm({ ...form, canConsent: checked })}
          label={tr('opd', 'may_consent_for_the_patient')}
        />
        <Checkbox
          checked={form.isPrimary}
          onCheckedChange={(checked) => setForm({ ...form, isPrimary: checked })}
          label={tr('opd', 'make_this_the_primary_guardian')}
        />
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={tr('opd', 'save_contact')} loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
